import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaPlus, FaEdit } from "react-icons/fa";
import {
  getCategories,
  createCategory,
  updateCategory,
} from "../api/apiServices";

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const ITEMS_PER_PAGE = 9;

  /* ================= FETCH ================= */
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await getCategories();
      setCategories(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= FILTER + PAGINATION ================= */
  const filtered = categories.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  const paginatedCategories = filtered.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  /* ================= CREATE / UPDATE ================= */
  const handleSubmit = async (data) => {
    try {
      if (editingCategory) {
        await updateCategory(editingCategory._id, data);
      } else {
        await createCategory(data);
      }

      setModalOpen(false);
      setEditingCategory(null);
      fetchCategories();
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="min-h-screen bg-gray-50 px-4 pb-20">
      {/* Header */}
      <div className="max-w-6xl mx-auto mt-10 flex justify-between items-center">
        <h1 className="text-3xl font-bold">Categories</h1>

        <button
          onClick={() => {
            setEditingCategory(null);
            setModalOpen(true);
          }}
          className="flex items-center gap-2 bg-orange-500 text-white px-5 py-2 rounded-lg hover:bg-orange-600"
        >
          <FaPlus /> Add Category
        </button>
      </div>

      {/* Search */}
      <div className="max-w-6xl mx-auto mt-6">
        <input
          placeholder="Search categories..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-full md:w-1/2 px-4 py-2 border rounded-lg"
        />
      </div>

      {/* Categories Grid */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-6xl mx-auto mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {paginatedCategories.map((cat) => (
          <div
            key={cat._id}
            className="bg-white rounded-xl shadow hover:shadow-lg transition"
          >
            <img
              src={cat.image}
              alt={cat.name}
              className="h-48 w-full object-cover rounded-t-xl"
            />

            <div className="p-5">
              <h3 className="font-bold text-lg">{cat.name}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {cat.description}
              </p>

              <div className="flex justify-between items-center mt-4">
                <span className="text-xs text-gray-400">
                  Updated {new Date(cat.updatedAt).toLocaleDateString()}
                </span>

                <button
                  onClick={() => {
                    setEditingCategory(cat);
                    setModalOpen(true);
                  }}
                  className="text-indigo-600 hover:text-indigo-800"
                >
                  <FaEdit />
                </button>
              </div>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Pagination */}
      <div className="flex justify-center gap-4 mt-10">
        <button
          disabled={page === 1}
          onClick={() => setPage((p) => p - 1)}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Previous
        </button>

        <span>
          Page {page} of {totalPages}
        </span>

        <button
          disabled={page === totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>

      {/* Create / Edit Modal */}
      {modalOpen && (
        <CategoryModal
          initialData={editingCategory}
          onClose={() => setModalOpen(false)}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}

/* ================= MODAL ================= */
function CategoryModal({ initialData, onClose, onSubmit }) {
  const [form, setForm] = useState({
    name: initialData?.name || "",
    description: initialData?.description || "",
    image: initialData?.image || "",
  });

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-md rounded-xl p-6">
        <h2 className="text-xl font-bold mb-4">
          {initialData ? "Edit Category" : "Create Category"}
        </h2>

        <div className="space-y-4">
          <input
            name="name"
            placeholder="Category name"
            value={form.name}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded"
          />

          <input
            name="image"
            placeholder="Image URL"
            value={form.image}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded"
          />

          <textarea
            name="description"
            placeholder="Description"
            value={form.description}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded"
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-gray-600">
            Cancel
          </button>
          <button
            onClick={() => onSubmit(form)}
            className="px-4 py-2 bg-orange-500 text-white rounded"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
